import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  address: "",
  subject: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const valid =
    form.name.trim() &&
    form.email.trim() &&
    form.phone.trim() &&
    form.subject.trim() &&
    form.message.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please fill in name, email, phone, subject and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Booking request sent. Warren will be in touch shortly.");
      setSuccess(true);
      setForm(initialForm);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let detail = "Something went wrong. Please try again or call us.";
      if (typeof raw === "string") detail = raw;
      else if (Array.isArray(raw) && raw[0]?.msg) detail = raw[0].msg;
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 md:py-40 bg-[#0A0A0A] overflow-hidden"
    >
      <div className="absolute -top-32 -left-40 w-[400px] h-[400px] rounded-full bg-[#D4AF37]/10 blur-[150px]" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-14">
          {/* Left: contact info */}
          <div className="lg:col-span-5">
            <p className="eyebrow reveal">05 — Get in Touch</p>
            <h2 className="font-display text-white font-light text-4xl md:text-6xl tracking-tighter mt-5 leading-[1.02] reveal">
              Bring us your{" "}
              <span className="gold-text italic">prized motor</span>.
            </h2>
            <p className="text-white/60 mt-6 max-w-md leading-relaxed reveal">
              Whether it’s a daily driver due for protection or a weekend
              showpiece in need of correction — request your booking and we’ll
              be in touch within the day.
            </p>

            <div className="mt-12 space-y-7">
              <a
                href={`tel:${SITE.phoneRaw}`}
                data-testid="contact-info-phone"
                className="flex items-start gap-4 group reveal"
              >
                <div className="w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/45">
                    Phone
                  </p>
                  <p className="font-display text-white text-xl mt-1 group-hover:text-[#D4AF37] transition">
                    {SITE.phone}
                  </p>
                </div>
              </a>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-info-email"
                className="flex items-start gap-4 group reveal"
              >
                <div className="w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/45">
                    Email
                  </p>
                  <p className="font-display text-white text-xl mt-1 group-hover:text-[#D4AF37] transition break-all">
                    {SITE.email}
                  </p>
                </div>
              </a>
              <div
                data-testid="contact-info-address"
                className="flex items-start gap-4 reveal"
              >
                <div className="w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/45">
                    Studio
                  </p>
                  <p className="font-display text-white text-xl mt-1 leading-snug">
                    {SITE.address}
                  </p>
                  <p className="text-[#D4AF37]/80 text-xs mt-2 tracking-wide">
                    Mobile service available
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="glass-card p-7 md:p-10 reveal"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Name" required>
                  <input
                    data-testid="contact-input-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    className="input-dark w-full px-4 py-3.5 text-sm"
                    placeholder="Your name"
                    required
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="input-dark w-full px-4 py-3.5 text-sm"
                    placeholder="you@email.com"
                    required
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    data-testid="contact-input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="input-dark w-full px-4 py-3.5 text-sm"
                    placeholder="021..."
                    required
                  />
                </Field>
                <Field label="Address">
                  <input
                    data-testid="contact-input-address"
                    type="text"
                    value={form.address}
                    onChange={update("address")}
                    className="input-dark w-full px-4 py-3.5 text-sm"
                    placeholder="For mobile service"
                  />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Subject" required>
                    <input
                      data-testid="contact-input-subject"
                      type="text"
                      value={form.subject}
                      onChange={update("subject")}
                      className="input-dark w-full px-4 py-3.5 text-sm"
                      placeholder="e.g. Ceramic coating enquiry"
                      required
                    />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="Message" required>
                    <textarea
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      rows={5}
                      className="input-dark w-full px-4 py-3.5 text-sm resize-none"
                      placeholder="Tell us about your vehicle and what you're after..."
                      required
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                <p className="text-white/40 text-xs leading-relaxed max-w-sm">
                  By submitting, you agree to be contacted by Cutting Edge
                  Detail regarding your enquiry.
                </p>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="btn-gold px-8 py-4 text-xs flex items-center justify-center gap-3 min-w-[200px] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending
                    </>
                  ) : success ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Sent
                    </>
                  ) : (
                    <>
                      Request Booking <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.3em] text-white/55 mb-2 inline-block">
        {label}
        {required && <span className="text-[#D4AF37] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
